import React, { useState } from 'react';
import { X, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { ManualGameDeposit } from '../../../types';
import { useRejectManualGameDeposit } from '../../../hooks/useApi';
import { LoadingSpinner } from '../../ui/Loading';
import { formatCurrency } from '../../../utils';
import { SUCCESS_MESSAGES } from '../../../constants';

interface RejectDepositModalProps {
  deposit: ManualGameDeposit | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const parseErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred while rejecting deposit';
};

const RejectDepositModal: React.FC<RejectDepositModalProps> = ({
  deposit,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const rejectMutation = useRejectManualGameDeposit();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deposit) return;

    if (!reason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      await rejectMutation.mutateAsync({
        transactionId: deposit.transactionId,
        data: { reason: reason.trim() },
      });
      toast.success(SUCCESS_MESSAGES.MANUAL_DEPOSIT_REJECTED);
      onSuccess?.();
      onClose();
      setReason('');
    } catch (error: any) {
      console.error('Reject deposit error:', error);
      const errorMessage = parseErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  if (!isOpen || !deposit) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Reject Manual Deposit</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Deposit Summary */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to reject this manual deposit request?
              </p>
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-bold text-red-700 text-lg">
                      {formatCurrency(deposit.amount)} {deposit.currency || 'USD'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Game</span>
                    <span className="font-medium">{deposit.gameProvider.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">User</span>
                    <span className="font-medium">{deposit.userEmail}</span>
                  </div>
                </div>
              </div>

              {/* Refund Notice */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  Funds will be <strong>returned immediately</strong> to the user&apos;s main wallet.
                </p>
              </div>
            </div>

            {/* Rejection Reason (required) */}
            <div>
              <label htmlFor="reason" className="block text-sm font-medium mb-2">
                Rejection Reason <span className="text-red-600">*</span>
              </label>
              <Input
                id="reason"
                type="text"
                placeholder="e.g., Game under maintenance, unable to deposit"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={1000}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                {reason.length}/1000 characters
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={rejectMutation.isLoading || !reason.trim()}
              >
                {rejectMutation.isLoading ? (
                  <div className="flex items-center">
                    <LoadingSpinner />
                    <span className="ml-2">Rejecting...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject Deposit
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RejectDepositModal;
